/* eslint-disable react/no-unknown-property */
import React, { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment, Grid, Text, Line } from "@react-three/drei";
import * as THREE from "three";

const Arrow = ({ vec, color, label }) => {
  const dir = new THREE.Vector3(vec[0], vec[1], 0).normalize();
  const len = Math.hypot(vec[0], vec[1]);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  const rot = new THREE.Euler().setFromQuaternion(q);
  return (
    <group>
      <mesh position={[vec[0] / 2, vec[1] / 2, 0]} rotation={rot}>
        <cylinderGeometry args={[0.05, 0.05, len, 12]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.4} metalness={0.5} roughness={0.4} />
      </mesh>
      <mesh position={[vec[0], vec[1], 0]} rotation={rot}>
        <coneGeometry args={[0.16, 0.35, 16]} />
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.5} metalness={0.5} roughness={0.4} />
      </mesh>
      <Text position={[vec[0] * 1.15, vec[1] * 1.15, 0.15]} fontSize={0.28} color={color} anchorX="center">
        {label}
      </Text>
    </group>
  );
};

const Parallelogram3D = ({ params }) => {
  const P = +params.P || 4;
  const Q = +params.Q || 3;
  const theta = ((+params.theta || 60) * Math.PI) / 180;

  const { P_vec, Q_vec, R_vec, R_mag } = useMemo(() => {
    const P_vec = [P, 0];
    const Q_vec = [Q * Math.cos(theta), Q * Math.sin(theta)];
    const R_vec = [P_vec[0] + Q_vec[0], P_vec[1] + Q_vec[1]];
    const R_mag = Math.hypot(R_vec[0], R_vec[1]);
    return { P_vec, Q_vec, R_vec, R_mag };
  }, [P, Q, theta]);

  const scale = 3 / Math.max(R_mag, 5);
  const sc = (v) => [v[0] * scale, v[1] * scale];
  const Ps = sc(P_vec), Qs = sc(Q_vec), Rs = sc(R_vec);

  // Parallelogram dashed sides
  const dashedA = [Ps, Rs];
  const dashedB = [Qs, Rs];

  return (
    <Canvas camera={{ position: [0, 0, 6], fov: 45 }} dpr={[1, 2]}>
      <color attach="background" args={["#020617"]} />
      <ambientLight intensity={0.6} />
      <pointLight position={[3, 3, 5]} intensity={1} color="#22d3ee" />

      <Grid args={[10, 10]} cellColor="#1e293b" sectionColor="#0891b2" position={[0, -3, 0]} rotation={[-Math.PI / 2, 0, 0]} fadeDistance={12} />

      {/* Origin marker */}
      <mesh>
        <sphereGeometry args={[0.06, 12, 12]} />
        <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.4} />
      </mesh>

      <Arrow vec={[Ps[0], Ps[1]]} color="#22d3ee" label={`P = ${P.toFixed(1)}`} />
      <Arrow vec={[Qs[0], Qs[1]]} color="#a855f7" label={`Q = ${Q.toFixed(1)}`} />
      <Arrow vec={[Rs[0], Rs[1]]} color="#f59e0b" label={`R = ${R_mag.toFixed(2)}`} />

      <Line points={[[dashedA[0][0], dashedA[0][1], 0], [dashedA[1][0], dashedA[1][1], 0]]} color="#475569" lineWidth={1.5} dashed dashSize={0.15} gapSize={0.1} />
      <Line points={[[dashedB[0][0], dashedB[0][1], 0], [dashedB[1][0], dashedB[1][1], 0]]} color="#475569" lineWidth={1.5} dashed dashSize={0.15} gapSize={0.1} />

      <Text position={[-3.5, 3, 0]} fontSize={0.22} color="#22d3ee" anchorX="left">
        {`θ = ${((theta * 180) / Math.PI).toFixed(0)}°`}
      </Text>

      <Environment preset="night" />
      <OrbitControls enablePan={false} minDistance={4} maxDistance={12} />
    </Canvas>
  );
};

export default Parallelogram3D;
